import { useEffect, useRef, useState, useCallback } from "react";

// Always proxy through nginx — same host/port as the page
function getWsBase() {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  return `${proto}//${window.location.host}/ws`;
}

function getToken() {
  try {
    const stored = JSON.parse(localStorage.getItem("sentinel_auth"));
    return stored?.access_token || "";
  } catch { return ""; }
}

export function useWebSocket(channel) {
  const [messages, setMessages] = useState([]);
  const [connected, setConnected] = useState(false);
  const ws = useRef(null);
  const reconnectTimer = useRef(null);

  const connect = useCallback(() => {
    if (ws.current?.readyState === WebSocket.OPEN) return;
    const token = getToken();
    if (!token) {
      // Token not ready yet (initial-load race with useAuth). Retry instead of
      // stranding the socket with no reconnect scheduled — the retry reads a fresh
      // token from storage once auth has populated it.
      reconnectTimer.current = setTimeout(connect, 3000);
      return;
    }
    const url = `${getWsBase()}/${channel}?token=${encodeURIComponent(token)}`;
    try {
      ws.current = new WebSocket(url);
    } catch (e) {
      reconnectTimer.current = setTimeout(connect, 3000);
      return;
    }

    ws.current.onopen = () => {
      setConnected(true);
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    };

    ws.current.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setMessages((prev) => [data, ...prev].slice(0, 200));
      } catch {}
    };

    ws.current.onclose = () => {
      setConnected(false);
      reconnectTimer.current = setTimeout(connect, 3000);
    };

    ws.current.onerror = () => {
      ws.current?.close();
    };
  }, [channel]);

  useEffect(() => {
    connect();
    return () => {
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      ws.current?.close();
    };
  }, [connect]);

  return { messages, latest: messages[0] || null, connected };
}

export function useLatestBySymbol(channel) {
  const { messages, connected } = useWebSocket(channel);
  const [bySymbol, setBySymbol] = useState({});

  useEffect(() => {
    const msg = messages[0];
    if (msg?.symbol) {
      setBySymbol((prev) => ({ ...prev, [msg.symbol]: msg }));
    }
  }, [messages]);

  return { bySymbol, connected };
}
